import type * as ozon from "@/api/ozonSupply";
import { pad2 } from "../format";
import type { StageActions } from "./ozonStage";
import { fmtSlot } from "./slotFormat";

/** Наблюдатель мест: слова, проверки и запреты экрана — чистыми функциями, их проверяет тест.
 *
 *  Наблюдатель живёт в портале, а тикает его Airflow раз в минуту. Экран отвечает на три вопроса
 *  менеджера: следит ли он ПРЯМО СЕЙЧАС, что видит датчик пункта и что решил последним.
 */

export const WATCH_STATUS_LABEL: Record<ozon.SlotWatchStatus, string> = {
  active: "на мониторинге",
  booked: "заявка создана",
  stopped: "снят",
  expired: "истёк",
};

/** Шаги тика словами. Незнакомый шаг показываем как есть — бэкенд добавит новый, увидим его слово. */
export const WATCH_STEP_LABEL: Record<ozon.SlotWatchStep, string> = {
  sensor: "датчик",
  probe: "проба",
  draft: "черновик",
  timeslots: "окна",
  order: "заявка",
  resolve: "сверка",
  state: "состояние",
  error: "ошибка",
};

export function stepLabel(step: string): string {
  return WATCH_STEP_LABEL[step as ozon.SlotWatchStep] ?? step;
}

/** Тик раз в минуту: молчит дольше трёх — DAG встал или портал не отвечает ему. */
export const TICK_STALE_MIN = 3;

/** Пределы формы — те же, что у бэкенда (`not_before_hours` 0…624 = 26 дней: окно площадки — 27 дней, `days` 1…14). */
export const HOURS_MAX = 624;
export const DAYS_MIN = 1;
export const DAYS_MAX = 14;
export const HOURS_DEFAULT = 24;
export const DAYS_DEFAULT = 7;

/** Наблюдатель молчит: текст предупреждения или null.
 *
 *  ⚠️ Только у активного: у снятого и истёкшего тиков и не должно быть. Тиков не было вовсе —
 *  отсчёт от постановки: «поставили час назад, а он ни разу не посмотрел» — та же поломка.
 */
export function tickStale(
  w: Pick<ozon.SlotWatchView, "status" | "last_tick_at" | "created_at">,
  now: number,
): string | null {
  if (w.status !== "active") return null;
  const since = w.last_tick_at ?? w.created_at;
  const min = Math.floor((now - new Date(since).getTime()) / 60_000);
  if (min <= TICK_STALE_MIN) return null;
  return w.last_tick_at
    ? `Наблюдатель не отвечает: последний тик ${ago(min)} назад (тикает раз в минуту)`
    : `Наблюдатель не отвечает: поставлено ${ago(min)} назад, а тиков не было ни одного`;
}

function ago(min: number): string {
  if (min < 60) return `${min} мин`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h} ч ${min % 60} мин` : `${Math.floor(h / 24)} дн`;
}

/** Чем наблюдатель видит окна пункта — одной фразой. */
export function sensorMode(s: ozon.SlotWatchSensor | null): string {
  if (!s) return "датчик ещё не выбран — тиков не было";
  switch (s.mode) {
    case "order":
      return `по заявке № ${s.order_id ?? "?"}${
        s.region ? ` (${s.region}${s.shipment_id != null ? `, поставка #${s.shipment_id}` : ""})` : ""
      }`;
    case "probe":
      return "пробы черновиком — живых заявок на пункте нет (раз в 15 мин на пункт)";
  }
}

/** Окна датчика: всего / подходящих / первое подходящее. null — датчик окна не считал. */
export function sensorSlots(s: ozon.SlotWatchSensor | null): string | null {
  if (!s || s.slots_total == null) return null;
  const parts = [`окон всего ${s.slots_total}`, `подходящих ${s.slots_fit ?? "?"}`];
  if (s.first_fit_from) parts.push(`первое подходящее ${fmtSlot(s.first_fit_from)} (время пункта сдачи)`);
  return parts.join(" · ");
}

/** «28.10 22:00» — местное время браузера (для времени пункта сдачи не годится — см. `fmtSlot`). */
export function fmtShort(d: Date): string {
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Время события журнала до секунд: тики идут раз в минуту, без секунд соседи сливаются. */
export function fmtEventAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${fmtShort(d)}:${pad2(d.getSeconds())}`;
}

/** С какого момента окна подходят, если проверять прямо сейчас. */
export function notBeforeAt(hours: number, now: number): string {
  return `≈ ${fmtShort(new Date(now + hours * 3_600_000))}`;
}

/** Пункт сдачи в форме: id, имя (обязательно для бэкенда) и тип. */
export interface WatchDropOff {
  id: number;
  name: string | null;
  type: string | null;
}

/** Ввод формы КАК ЕСТЬ: числа — строками из полей. */
export interface WatchForm {
  dropOff: WatchDropOff | null;
  hours: string;
  days: string;
}

/** Ввод → тело запроса, или почему отправлять нельзя (по полям). */
export type WatchFormResult =
  | { body: ozon.SlotWatchStart; errors?: undefined }
  | { body?: undefined; errors: Partial<Record<keyof WatchForm, string>> };

function intIn(v: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v.trim());
  return n >= min && n <= max ? n : null;
}

export function parseWatchForm(f: WatchForm): WatchFormResult {
  const errors: Partial<Record<keyof WatchForm, string>> = {};
  if (!f.dropOff) errors.dropOff = "выберите пункт сдачи";
  // ⚠️ имя бэкенду обязательно (по нему он находит точку у площадки); у старых записей, выбранных до
  // 12.10, его нет — такой пункт надо выбрать заново из списка или поиском, а не слать пустым
  else if (!f.dropOff.name)
    errors.dropOff = "у этого пункта не записано имя — выберите его из списка или найдите поиском";
  const hours = intIn(f.hours, 0, HOURS_MAX);
  if (hours === null) errors.hours = `целое число часов от 0 до ${HOURS_MAX}`;
  const days = intIn(f.days, DAYS_MIN, DAYS_MAX);
  if (days === null) errors.days = `целое число дней от ${DAYS_MIN} до ${DAYS_MAX}`;
  if (Object.keys(errors).length > 0 || !f.dropOff?.name || hours === null || days === null)
    return { errors };
  return {
    body: {
      drop_off_id: f.dropOff.id,
      drop_off_name: f.dropOff.name,
      drop_off_type: f.dropOff.type,
      not_before_hours: hours,
      days,
    },
  };
}

/** Поставить на мониторинг можно, пока заявки нет и она не отправлена: дальше окно уже взято. */
export function canWatch(d: Pick<ozon.OzonDirection, "order_id" | "state">): boolean {
  return !d.order_id && d.state !== "ordering" && d.state !== "ordered";
}

/** Почему ручные действия погашены — та же причина, что в 409 бэкенда. */
export const WATCH_LOCK = "на мониторинге мест — снимите мониторинг";

/** Действия, которые бэкенд отвергает 409, пока мониторинг активен: черновик, сверка заявки, окна (выбор
 *  окна создаёт заявку) и отмена заявки. Смену пункта гасит экран отдельно — это не действие стадии. */
const LOCKED: ReadonlySet<string> = new Set(["draft", "slots", "cancel", "resolve"]);

/** Погасить с причиной, а не спрятать: пропавшая кнопка читалась бы как поломка экрана. */
export function lockForWatch(a: StageActions): StageActions {
  const lock = <T extends { key: string; disabled?: string }>(x: T): T =>
    LOCKED.has(x.key) ? { ...x, disabled: WATCH_LOCK } : x;
  return { primary: a.primary ? lock(a.primary) : null, more: a.more.map(lock) };
}

/** События, которые показывает фильтр «только ошибки». */
export function isErrorEvent(e: Pick<ozon.SlotWatchEventView, "ok" | "step">): boolean {
  return !e.ok || e.step === "error";
}
